import type { ISerialize } from '@/objects/ISerialize';
import { QueueStatus } from './QueueStatus';
import { formatDateIso } from '@/utilities/dateutils';

export class SystemStatus implements ISerialize {
    public IsOnline: boolean;

    public ListenAddress: string | null;

    public Queues: QueueStatus[];

    public SystemName: string;

    public Time: Date;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        IsOnline?: boolean;
        ListenAddress?: string | null;
        Queues?: QueueStatus[];
        SystemName?: string | null;
        Time?: Date;
    }) {
        this.IsOnline = values.IsOnline ?? false;

        this.ListenAddress = values.ListenAddress ?? '';

        this.Queues = values.Queues ?? [];

        this.SystemName = values.SystemName ?? '';

        this.Time = values.Time ?? new Date('1970-01-01T00:00:00.000');
    }

    public serialize(): string {
        return (
            '{' +
            `"IsOnline": ${JSON.stringify(this.IsOnline)}` +
            ',' +
            `"ListenAddress": ${JSON.stringify(this.ListenAddress)}` +
            ',' +
            `"Queues": [ ${this.Queues.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"SystemName": ${JSON.stringify(this.SystemName)}` +
            ',' +
            `"Time": ${'"' + formatDateIso(this.Time) + '"'}` +
            '}'
        );
    }

    /**
     * Creates an instance of SystemStatus from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        IsOnline?: boolean;
        ListenAddress?: string | null;
        Queues?: QueueStatus[];
        SystemName?: string | null;
        Time?: Date;
    }): SystemStatus {
        return new SystemStatus(values);
    }

    public static _FromServerJson(json: any): SystemStatus {
        return new SystemStatus({
            IsOnline: json.IsOnline,
            ListenAddress: json.ListenAddress ?? '',
            Queues: json.Queues?.map((x: any) => QueueStatus._FromServerJson(x)),
            SystemName: json.SystemName ?? '',
            Time: json.Time instanceof Date ? json.Time : new Date(json.Time),
        });
    }

    /**
     * Creates an instance of SystemStatus from another instance of SystemStatus.
     * @param other The SystemStatus you want to clone
     * @returns A cloned SystemStatus
     */
    public static FromOther(other: SystemStatus): SystemStatus {
        return SystemStatus._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return SystemStatus.FromOther(this);
    }

    static NAME_OF_CLASS = 'SystemStatus';
}
